<template>
  <template
    v-if="
      (toBool(topic.new_votes) && currentSupport !== null) ||
      (toBool(topic.allow_voting_per_thought) && toBool(thought.allow_voting))
    "
  >
    <div v-if="message.infoText" class="mb-2" role="alert">
      <Message :closable="false" severity="info">
        <span style="white-space: pre-line">
          {{ message.infoText }}
        </span>
      </Message>
    </div>
    <div v-if="message.errorMessage" class="alert alert-danger" role="alert">
      <span>
        {{ message.errorMessage }}
      </span>
    </div>
    <button
      v-if="topic.votetype === 'support' || topic.votetype === 'weighted_support'"
      class="btn wdw-thought-btn wdw-thought-support-button wdw-thought-support-button-up"
      :disabled="isPostingVote"
      :class="supportButtonClasses"
      @click="onSupportClicked"
      type="button"
      :title="supportButton_tooltips_titel"
      v-tooltip.bottom="supportButton_tooltips_titel"
    >
      <font-awesome-icon :icon="['far', 'thumbs-up']" />
      <span class="visually-hidden">{{ supportButton_tooltips_titel }}</span>
    </button>
    <span v-else-if="topic.votetype === 'nnp'">
      <button
        :class="nnpAgreeButtonClasses"
        class="btn wdw-thought-btn wdw-thought-support-button wdw-thought-support-button-up mr"
        :disabled="isPostingVote"
        @click="onNNPProClicked"
        type="button"
        :title="nnpAgreeButton_tooltips_titel"
        v-tooltip.bottom="nnpAgreeButton_tooltips_titel"
      >
        <font-awesome-icon :icon="['far', 'thumbs-up']" aria-hidden="true" />
        <span class="visually-hidden">{{ nnpAgreeButton_tooltips_titel }}</span>
      </button>
      <button
        :class="nnpDisagreeButtonClasses"
        class="btn wdw-thought-btn wdw-thought-support-button wdw-thought-support-button-down"
        :disabled="isPostingVote"
        @click="onNNPConClicked"
        type="button"
        :title="nnpDisagreeButton_tooltips_titel"
        v-tooltip.bottom="nnpDisagreeButton_tooltips_titel"
      >
        <font-awesome-icon :icon="['far', 'thumbs-down']" aria-hidden="true" />
        <span class="visually-hidden">{{ nnpDisagreeButton_tooltips_titel }}</span>
      </button>
    </span>
    <vote-stars
      v-else-if="topic.votetype == 'stars' && topic.new_votes"
      :voteStatus="Number(currentSupport.vote)"
      :canVote="true"
      @vote-value="checkAndInitiateVote"
    />

    <OverlayPanel
      v-if="!isLoggedIn"
      ref="loginOP"
      :showCloseIcon="true"
      :dismissable="true"
      appendTo="body"
      class="wdw-widgets"
    >
      <p>{{ domainStore.translate('discuss.thought.message.vote-requires-login') }}</p>
      <Login :domainid="domainid" />
    </OverlayPanel>
  </template>
</template>
<script>
import useConverter from '@/composables/general/useConverter';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import thoughtRepository from '@/api/thoughtRepository';
import { domainStore } from '@/store/domain-store';
import { computed, onMounted, reactive, ref, toRefs, watch } from '@vue/runtime-core';
import { userStore } from '@/store/user-store';
import OverlayPanel from 'primevue/overlaypanel';
import Message from 'primevue/message';
import useTopicConfig from '@/composables/topic/useTopicConfig';
import useSupportEvents from '@/composables/support/useSupportEvents';
import useTopicTerm from '@/composables/topic/useTopicTerm';
import VoteStars from '@/components/discuss/thought/VoteStars.vue';
import Login from '@/components/user/Login.vue';
import { projectStore } from '@/store/project-store';
import { thoughtStore } from '@/store/thought-store';
import useLocalCache from '@/composables/general/useLocalCache';

export default {
  name: 'thought-support-controls',

  props: {
    topic: {
      required: true,
      type: Object,
    },
    thought: {
      required: true,
      type: Object,
    },
    // hasOwnComment: {
    //   required: false,
    //   type: Boolean,
    //   default: false,
    // },
    fromList: {
      type: Boolean,
      required: false,
      default: false,
    },
    signalRefresh: {
      required: false,
      type: Number,
      default: 0,
    },
  },

  components: {
    OverlayPanel,
    Message,
    VoteStars,
    Login,
  },

  emits: ['support-requires-comment', 'support-vote-sent'],

  setup(props, context) {
    const { topic, thought, signalRefresh, fromList } = toRefs(props);
    const { storeRedirUrlInCache } = useLocalCache();
    const { toBool } = useConverter();
    const { l } = useLocalizeMessages();
    const { emitVoted } = useSupportEvents(context);
    const message = reactive({
      infoText: '',
      errorMessage: '',
    });
    // Contains the current support value the user has
    const currentSupport = ref(null);
    const showCommentBoxForVote = ref(false);
    const projectSupport = ref(0);
    const topicSupport = ref(0);
    // UI references
    const loginOP = ref();
    const { canVote, canVoteAfterLogin } = useTopicConfig();
    const { topicInThis } = useTopicTerm();

    // API call state
    const isPostingVote = ref(false);
    let isRetrievingVote = false;

    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });

    const hasOwnComment = computed(() => {
      return thought.value.first_comments?.my ? true : false;
    });
    const isVirtualuser = computed(() => {
      return userStore.isVirtualUser();
    });

    const domainid = computed(() => {
      return domainStore.getState().domainid;
    });

    const supportButtonClasses = computed(() => {
      return {
        voted: currentSupport.value != null && currentSupport.value.vote >= 1,
        disabled: isPostingVote.value,
      };
    });
    const nnpAgreeButtonClasses = computed(() => {
      return {
        voted: currentSupport.value != null && currentSupport.value.vote === 1,
        disabled: isPostingVote.value,
      };
    });
    const nnpDisagreeButtonClasses = computed(() => {
      return {
        disagree: currentSupport.value != null && currentSupport.value.vote === -1,
        disabled: isPostingVote.value,
      };
    });
    const commentBoxForVote = computed(() => {
      return topic.value.commented_voting !== 'never' && !hasOwnComment.value;
    });
    const supportButton_tooltips_titel = computed(() => {
      return currentSupport.value != null && currentSupport.value.vote >= 1
        ? domainStore.translate('discuss.thought.voting.thumbs.status.voted')
        : domainStore.translate('discuss.thought.voting.thumbs.status.not-voted');
    });
    const nnpAgreeButton_tooltips_titel = computed(() => {
      return currentSupport.value != null && currentSupport.value.vote === 1
        ? domainStore.translate('discuss.thought.voting.thumbs.status.voted-pro')
        : domainStore.translate('discuss.thought.voting.thumbs.status.not-voted-pro');
    });
    const nnpDisagreeButton_tooltips_titel = computed(() => {
      return currentSupport.value != null && currentSupport.value.vote === -1
        ? domainStore.translate('discuss.thought.voting.thumbs.status.voted-con')
        : domainStore.translate('discuss.thought.voting.thumbs.status.not-voted-con');
    });
    const projectMaxVotes = computed(() => {
      return projectStore.getState().project.max_votes_per_user
        ? projectStore.getState().project.max_votes_per_user
        : 0;
    });
    const topicMaxVotes = computed(() => {
      return topic.value.max_votes_per_user ? topic.value.max_votes_per_user : 0;
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });
    const weightedPointsWord = computed(() => {
      if (project.value && project.value.config?.weighted_support_word) {
        return project.value.config.weighted_support_word;
      }
      return 'Punkte';
    });
    // METHODS
    const updateThoughtwithVote = () => {
      thoughtStore.fetchThought(thought.value.id, true);
    };
    const doSendVote = (event, voteValue) => {
      let serverVoteError = '';
      // reset messages
      message.infoText = '';
      message.errorMessage = '';
      thoughtRepository
        .postVote(thought.value.id, voteValue)
        .then(resp => {
          //Get the current amount of votes for each Project and topic
          projectSupport.value = resp.project_support;
          topicSupport.value = resp.topic_support;
          if (voteValue === 99) {
            if (topic.value.votetype === 'weighted_support') {
              // weighted_support uses sum, but one vote can have a weighted value (one vote = 2, another vote = 4, etc.)
              message.infoText = domainStore.translate('discuss.thought.voting.weighted.message.take-back-vote', {
                word: weightedPointsWord.value,
              });
            } else {
              message.infoText = domainStore.translate('discuss.thought.voting.thumbs.message.take-back-vote');
            }
          }
          // Show info messages when max vote value is setup (for both project or topic)
          else if (projectMaxVotes.value > 0) {
            if (resp.project_limit_sum) {
              // if project_limit_sum exists in response, limit_type==sum
              if (topic.value.votetype === 'weighted_support') {
                // weighted_support uses sum, but one vote can have a weighted value (one vote = 2, another vote = 4, etc.)
                // message.infoText += `Sie haben ${resp.project_limit_sum.sum} von ${projectMaxVotes.value} ${weightedPointsWord.value} vergeben. Ihre Stimme zählt direkt. Wenn Sie es sich doch noch einmal anders überlegen, können Sie die Stimme mit einem Klick auf "Daumen hoch" wieder zurücknehmen.`;
                message.infoText += domainStore.translate(
                  'discuss.thought.voting.weighted.message.limit-sum-status-info',
                  {
                    votes: resp.project_limit_sum.sum,
                    max: projectMaxVotes.value,
                    word: weightedPointsWord.value,
                  }
                );
              } else {
                // default sum (sum of positive & negative counts towards max)
                // message.infoText += `Sie haben im Bereich "${topic.value.project.name}" bereits ${resp.project_limit_sum.positive_votes} Mal positiv und ${resp.project_limit_sum.negative_votes} Mal negativ abgestimmt. Ihr Abstimmungsergebnis beträgt daher ${resp.project_limit_sum.sum}. In Summe darf Ihr Abstimmungsergebnis nicht höher als ${projectMaxVotes.value} sein.`;
                message.infoText += domainStore.translate(
                  'discuss.thought.voting.thumbs.message.limit-sum-status-info',
                  {
                    name: topic.value.project.name,
                    pro_votes: resp.project_limit_sum.positive_votes,
                    con_votes: resp.project_limit_sum.negative_votes,
                    sum_votes: resp.project_limit_sum.sum,
                    max_votes: projectMaxVotes.value,
                  }
                );
              }
            } else {
              // else limit_type==count (each vote counts towards max)
              // message.infoText = `Sie haben in diesem Projekt ${projectSupport.value} mal abgestimmt und können maximal ${projectMaxVotes.value} Mal abstimmen.`;
              message.infoText += domainStore.translate(
                'discuss.thought.voting.thumbs.message.limit-count-status-info',
                {
                  vote_count: projectSupport.value,
                  max_votes: projectMaxVotes.value,
                }
              );
            }
          } else if (topicMaxVotes.value > 0) {
            if (message.infoText) {
              // add empty space if this is the second info message
              message.infoText += '\n\n';
            }
            if (resp.topic_limit_sum) {
              // if topic_limit_sum exists in response, limit_type==sum
              if (topic.value.votetype === 'weighted_support') {
                // weighted_support uses sum, but one vote can have a weighted value (one vote = 2, another vote = 4, etc.)
                // message.infoText += `Sie haben ${resp.topic_limit_sum.sum} von ${topicMaxVotes.value} ${weightedPointsWord.value} vergeben. Ihre Stimme zählt direkt. Wenn Sie es sich doch noch einmal anders überlegen, können Sie die Stimme mit einem Klick auf "Daumen hoch" wieder zurücknehmen.`;
                message.infoText += domainStore.translate(
                  'discuss.thought.voting.weighted.message.limit-sum-status-info',
                  {
                    votes: resp.topic_limit_sum.sum,
                    max: topicMaxVotes.value,
                    word: weightedPointsWord.value,
                  }
                );
              } else {
                // default sum (sum of positive & negative counts towards max)
                // message.infoText += `Sie haben im Bereich "${topic.value.title}" bereits ${resp.topic_limit_sum.positive_votes} Mal positiv und ${resp.topic_limit_sum.negative_votes} Mal negativ abgestimmt. Ihr Abstimmungsergebnis beträgt daher ${resp.topic_limit_sum.sum}. In Summe darf Ihr Abstimmungsergebnis nicht höher als ${topicMaxVotes.value} sein.`;
                message.infoText += domainStore.translate(
                  'discuss.thought.voting.thumbs.message.limit-sum-status-info',
                  {
                    name: topic.value.title,
                    pro_votes: resp.topic_limit_sum.positive_votes,
                    con_votes: resp.topic_limit_sum.negative_votes,
                    sum_votes: resp.topic_limit_sum.sum,
                    max_votes: topicMaxVotes.value,
                  }
                );
              }
            } else {
              // else limit_type==count (each vote counts towards max)
              // message.infoText += `Sie haben in ${topicInThis()} ${topicSupport.value} mal abgestimmt und können maximal ${topicMaxVotes.value} Mal abstimmen.`;
              message.infoText += domainStore.translate(
                'discuss.thought.voting.thumbs.message.limit-count-status-info',
                {
                  vote_count: topicSupport.value,
                  max_votes: topicMaxVotes.value,
                }
              );
            }
          }
          currentSupport.value = resp.support;
          isPostingVote.value = false;
          emitVoted(currentSupport.value);
          updateThoughtwithVote();
          projectStore.setCurrentVoteStatus(resp);
        })
        .catch(err => {
          // Error sending vote
          const errMsg = err.response.data.message;
          serverVoteError = l(errMsg);
          isPostingVote.value = false;
          message.errorMessage = serverVoteError;
          // evaluate various server error messages to human readable errors
          if (serverVoteError === 'vote limit for the topic has been reached') {
            // message.errorMessage = `Sie haben bereits das Maximum von ${
            //   topicMaxVotes.value
            // } Stimmen in ${topicInThis()} erreicht. Ziehen sie eine andere Stimme zurück, um erneut abstimmen zu können.`;
            message.errorMessage = domainStore.translate('discuss.thought.voting.thumbs.message.limit-count-exceeded', {
              max_votes: topicMaxVotes.value,
            });
          } else if (serverVoteError === 'vote limit for the project has been reached') {
            // message.errorMessage = `Sie haben bereits das Maximum von ${projectMaxVotes.value} Stimmen in diesem Projekt erreicht. Ziehen sie eine andere Stimme zurück, um erneut abstimmen zu können.`;
            message.errorMessage = domainStore.translate('discuss.thought.voting.thumbs.message.limit-count-exceeded', {
              max_votes: projectMaxVotes.value,
            });
          } else if (serverVoteError == 'vote sum limit for the topic has been reached') {
            if (topic.value.votetype === 'weighted_support') {
              // message.errorMessage = `Sie haben das Maximum von ${topicMaxVotes.value} ${weightedPointsWord.value} in ${topicInThis()} erreicht. Ziehen sie andere ${weightedPointsWord.value} zurück, um erneut abstimmen zu können.`;
              message.errorMessage = domainStore.translate(
                'discuss.thought.voting.weighted.message.limit-sum-exceeded',
                {
                  max_votes: topicMaxVotes.value,
                  word: weightedPointsWord.value,
                }
              );
            } else {
              // message.errorMessage = `Sie haben das maximale Abstimmungsergebnis von ${topicMaxVotes.value} in ${topicInThis()} erreicht. Sie können Stimmen zurückziehen oder weitere negative Stimmen vergeben, um weiter abstimmen zu können.`;
              message.errorMessage = domainStore.translate('discuss.thought.voting.thumbs.message.limit-sum-exceeded', {
                max_votes: topicMaxVotes.value,
              });
            }
          } else if (serverVoteError == 'vote sum limit for the project has been reached') {
            if (topic.value.votetype === 'weighted_support') {
              // message.errorMessage = `Sie haben das Maximum von ${projectMaxVotes.value} ${weightedPointsWord.value} in diesem Projekt erreicht. Ziehen sie andere ${weightedPointsWord.value} zurück, um erneut abstimmen zu können.`;
              message.errorMessage = domainStore.translate(
                'discuss.thought.voting.weighted.message.limit-sum-exceeded',
                {
                  max_votes: projectMaxVotes.value,
                  word: weightedPointsWord.value,
                }
              );
            } else {
              // message.errorMessage = `Sie haben das maximale Abstimmungsergebnis von ${projectMaxVotes.value} in diesem Projekt erreicht. Sie können Stimmen zurückziehen oder weitere negative Stimmen vergeben, um weiter abstimmen zu können.`;
              message.errorMessage = domainStore.translate('discuss.thought.voting.thumbs.message.limit-sum-exceeded', {
                max_votes: projectMaxVotes.value,
              });
            }
          }
        });
    };

    const refreshVote = () => {
      if (!isRetrievingVote) {
        isRetrievingVote = true;
        if (isLoggedIn.value || isVirtualuser.value) {
          thoughtRepository
            .getCurrentVote(thought.value.id)
            .then(resp => {
              currentSupport.value = resp;
              isRetrievingVote = false;
            })
            .catch(err => {
              // Error getting current vote. We assume it's empty
              currentSupport.value = { type: topic.value.votetype, vote: null };
              isRetrievingVote = false;
            });
        } else {
          currentSupport.value = { type: topic.value.votetype, vote: null };
          isRetrievingVote = false;
        }
      }
    };
    const checkAndInitiateVote = (event, voteValue) => {
      if (canVote(topic.value)) {
        if (!isPostingVote.value) {
          isPostingVote.value = true;

          if (
            !hasOwnComment.value &&
            (topic.value.commented_voting === 'force' || topic.value.commented_voting === 'optional')
          ) {
            // We need to trigger comment form for this. Comment form should be outside the component
            // We only pass the votevalue when the comment is required.
            // The voting will be executed by comment form in this case.
            context.emit('support-requires-comment', {
              commented_voting: topic.value.commented_voting,
              voteValue: voteValue,
            });
            isPostingVote.value = false;
          }

          // For commented_voting other than "force" or if user has already commented on it, we send the vote
          if (topic.value.commented_voting !== 'force' || hasOwnComment.value) {
            //if user clicks on currently voted star, delete vote
            if (voteValue === currentSupport.value.vote) {
              doSendVote(event, 99);
              //else send vote
            } else {
              doSendVote(event, voteValue);
            }
          }
        }
      } else if (canVoteAfterLogin(topic.value)) {
        loginOP.value.show(event);
        storeRedirUrlInCache('thought-vote', { thoughtid: thought.value.id });
      } else {
        console.error('error: cannot support, but support button is clicked anyway!');
      }
    };

    const onSupportClicked = event => {
      event.stopPropagation();
      // calculate vote pt
      let points = 1;
      if (topic.value.votetype === 'weighted_support') {
        points = thought.value.vote_weight ? thought.value.vote_weight : 1;
      }
      const voteValue = currentSupport.value == null || currentSupport.value.vote == 0 ? points : 99;
      checkAndInitiateVote(event, voteValue);
      // blur and set focus again for screenreaders
      event.target.blur();
      event.target.focus();
    };

    const onNNPProClicked = event => {
      event.stopPropagation();
      const voteValue = currentSupport.value == null ? 1 : currentSupport.value.vote == 1 ? 99 : 1;
      checkAndInitiateVote(event, voteValue);
      // blur and set focus again for screenreaders
      event.target.blur();
      event.target.focus();
    };
    const onNNPConClicked = event => {
      event.stopPropagation();
      const voteValue = currentSupport.value == null ? -1 : currentSupport.value.vote == -1 ? 99 : -1;
      checkAndInitiateVote(event, voteValue);
      // blur and set focus again for screenreaders
      event.target.blur();
      event.target.focus();
    };
    const useThoughtData = () => {
      currentSupport.value = thought.value.user_current_vote;
    };
    // WATCHES
    watch(
      () => userStore.getState().user,
      (newUser, oldUser) => {
        // refresh the vote state again when user is logged-in/out
        if ((newUser == null && oldUser) || (newUser && oldUser == null) || newUser?.id != oldUser?.id) {
          refreshVote();
        }
      }
    );
    watch(signalRefresh, () => {
      // Refresh signal from other components that use this
      refreshVote();
    });
    watch(thought, () => {
      useThoughtData();
    });
    // Lifecycle hooks
    onMounted(() => {
      // Check user's vote when this component is mounted
      if (!fromList.value) {
        refreshVote();
      } else {
        useThoughtData();
      }
    });

    return {
      domainStore,
      // Methods
      toBool,
      onSupportClicked,
      onNNPProClicked,
      onNNPConClicked,
      checkAndInitiateVote,
      // VARS
      currentSupport,
      loginOP,
      showCommentBoxForVote,
      isPostingVote,
      message,
      hasOwnComment,
      // Computed
      supportButtonClasses,
      domainid,
      isLoggedIn,
      nnpAgreeButtonClasses,
      nnpDisagreeButtonClasses,
      commentBoxForVote,
      supportButton_tooltips_titel,
      nnpAgreeButton_tooltips_titel,
      nnpDisagreeButton_tooltips_titel,
    };
  },
};
</script>
<style scoped lang="scss">
.btn.wdw-thought-btn {
  &.voted {
    background-color: var(--wdw-btn-positive-color);
    color: var(--wdw-btn-positive-contrast-color, #fff);
  }
  &.disagree {
    background-color: var(--wdw-btn-negative-color);
    color: var(--wdw-btn-negative-contrast-color, #fff);
  }
}
</style>
